import { useState } from "react"
import { Text } from "@smooth/design-system-components"
import { ContactLogPanel } from "../../components/ContactLogPanel"
import { ContractDetailsPanel, PromisePanel } from "../../components/ContractDetailsPanel"
import { DelinquencySummaryPanel } from "../../components/DelinquencySummaryPanel"
import { Panel } from "../../components/shared"
import { UserInfoPanel } from "../../components/UserInfoPanel"
import { MemoItem } from "../../components/UserMemosPanel"
import {
  scenarioCaseItem,
  scenarioChatMessages,
  scenarioContactLogs,
  scenarioMemos,
} from "../caseFacts"
import type { EvaluationCase, Scenario, Terminals } from "../types"
import { ChatTranscript } from "./CaseDetailParts"
import { CaseFrame, CaseHeader, ReviewCard } from "./CaseFrame"
import type { CaseFrameSlots } from "./CaseFrame"
import { ExpectedPathText } from "./ExpectedPath"
import { ExpectationSummary } from "./ExpectationView"
import { ClampedText, TerminalName } from "./parts"

/** 検証ケースにしか無い情報（期待する対応・期待の形・期待する道筋・メモ）。AI には渡さない */
export function EvaluationExpectation({
  record,
  terminals,
}: {
  record: EvaluationCase
  terminals: Terminals
}) {
  const path = record.scenario.expectedPath
  return (
    <section aria-label="期待とメモ" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline gap-3">
        <Text as="span" size="sm" weight="bold">
          期待する対応
        </Text>
        <TerminalName id={record.expectedTerminal} terminals={terminals} />
      </div>
      <ExpectationSummary
        expectation={record.expectation}
        expectedTerminal={record.expectedTerminal}
      />
      {!!path?.length && (
        <div className="flex flex-col gap-1">
          <Text size="sm" weight="bold">
            期待する道筋
          </Text>
          <ExpectedPathText path={path} />
        </div>
      )}
      {record.checkpoints.trim() && (
        <div className="flex flex-col gap-1">
          <Text size="sm" weight="bold">
            メモ
          </Text>
          <ClampedText>{record.checkpoints}</ClampedText>
        </div>
      )}
    </section>
  )
}

/** メモ欄。督促画面のメモ欄と同じ見え方で、古い順に並べる。事実メモ（古い形式のケース）は基準日時のメモとして最後に出す */
export function MemosPanel({ scenario }: { scenario: Scenario }) {
  const memos = scenarioMemos(scenario)
  return (
    <Panel title="メモ（タイトル・本文・日時が AI に渡ります）">
      {memos.length > 0 ? (
        <ul className="flex flex-col gap-3">
          {memos.map((memo, index) => (
            <MemoItem key={index} memo={memo} />
          ))}
        </ul>
      ) : (
        <p className="text-[1.3rem] text-black">メモはまだありません。</p>
      )}
    </Panel>
  )
}

/** 右のジャンプバーに添える件数（メモ・交渉記録） */
export const caseSectionCounts = (scenario: Scenario) => ({
  memos: scenarioMemos(scenario).length,
  contact_logs: scenarioContactLogs(scenario).length,
})

/**
 * ケースの読み取り表示。督促ケース詳細と同じ外枠・部品（左に督促チャット、
 * 右にユーザー情報・遅延状況・契約・メモ・交渉記録）で、AI に渡る入力をオペレーターが普段見ている見え方で読めるようにする。
 * 左は督促チャットだけにして、期待・生成・結果（「期待」）と支払約束は右のペインの先頭に置く
 */
export function CaseReadView({
  record,
  terminals,
  ...slots
}: {
  record: EvaluationCase
  terminals: Terminals
} & CaseFrameSlots) {
  const { scenario } = record
  const item = scenarioCaseItem(scenario)
  const facts = item.facts!
  const [selectedContractId, setSelectedContractId] = useState<number | null>(null)
  return (
    <CaseFrame
      resetKey={`read-${record.id}`}
      header={<CaseHeader record={record} {...slots} />}
      counts={caseSectionCounts(scenario)}
      chat={<ChatTranscript messages={scenarioChatMessages(scenario)} />}
      rightTop={
        <>
          {slots.rightHead}
          <ReviewCard>
            <EvaluationExpectation record={record} terminals={terminals} />
            {slots.review}
          </ReviewCard>
          <PromisePanel facts={facts} caseId={0} historyInitiallyOpen />
        </>
      }
      sections={{
        user: <UserInfoPanel facts={facts} userId={0} showUserLink={false} />,
        delinquency: <DelinquencySummaryPanel facts={facts} />,
        contracts: (
          <ContractDetailsPanel
            facts={facts}
            selectedContractId={selectedContractId}
            onSelectContract={setSelectedContractId}
            readOnly
          />
        ),
        memos: <MemosPanel scenario={scenario} />,
        contact_logs: <ContactLogPanel caseItem={item} onUpdated={() => undefined} readOnly />,
      }}
    />
  )
}
